// Tài khoản ngân hàng nhận tiền — dùng chung cho Cài đặt › Ngân hàng và màn
// "Hoàn tất thông tin tài khoản". Server chỉ lưu số tài khoản đã che (4 số cuối).

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { X } from 'lucide-react'
import { del, get, post } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { toBankingUnaccent } from '@/lib/format'
import { invalidateProfileStatus } from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { IndexBadge } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BankLogo } from '@/components/ui/bank-logo'
import { confirm } from '@/components/ui/confirm'
import { FieldRow } from './FieldRow'

export interface Bank {
  id: string; bank: string; accountMasked: string; holder: string
  via: string; isDefault: boolean; createdAt: string
}

/** Khớp KNOWN_BANKS của POST /banks. */
export const BANK_OPTIONS = ['Vietcombank', 'Techcombank', 'ACB', 'MB Bank', 'BIDV', 'VietinBank', 'VPBank', 'Sacombank', 'TPBank', 'HDBank', 'SHB', 'Agribank']

type BankForm = { bank: string; accountNumber: string; holder: string; via: 'sepay' | 'manual'; isDefault: boolean }

const emptyForm = (holder = ''): BankForm => ({ bank: 'Vietcombank', accountNumber: '', holder, via: 'sepay', isDefault: false })

export function BankAccountsManager({
  intro = true,
  readOnly = false,
}: {
  /** Tiêu đề + mô tả đầu khối (Cài đặt). Màn hoàn tất thông tin tự có tiêu đề bước. */
  intro?: boolean
  /** Chỉ xem — thành viên không có quyền thêm / xoá tài khoản. */
  readOnly?: boolean
}) {
  const qc = useQueryClient()
  const auth = getAuthState()
  // Gợi ý tên chủ tài khoản = họ tên chủ tài khoản vừa khai (nếu có), viết không dấu như ngân hàng in.
  const suggestedHolder = auth.name ? toBankingUnaccent(auth.name) : ''

  const list = useQuery({ queryKey: ['banks'], queryFn: () => get<{ banks: Bank[] }>('/banks') })
  const banks = list.data?.banks ?? []
  const isEmpty = list.isSuccess && banks.length === 0
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<BankForm>(() => emptyForm(suggestedHolder))

  // Màn hoàn tất thông tin: chưa có tài khoản nào thì mở sẵn form.
  useEffect(() => {
    if (!intro && !readOnly && isEmpty) setShowForm(true)
  }, [intro, readOnly, isEmpty])

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['banks'] })
    qc.invalidateQueries({ queryKey: ['ctv-reconcile-overview'] })
    qc.invalidateQueries({ queryKey: ['agency-reconcile-overview'] })
    void invalidateProfileStatus()
  }

  const accountError = form.accountNumber && !/^\d{4,20}$/.test(form.accountNumber) ? 'Số tài khoản gồm 4–20 chữ số' : null

  const create = useMutation({
    mutationFn: () => {
      if (!/^\d{4,20}$/.test(form.accountNumber)) throw new Error('Số tài khoản gồm 4–20 chữ số')
      if (!form.holder.trim()) throw new Error('Vui lòng nhập tên chủ tài khoản')
      return post('/banks', {
        ...form,
        holder: toBankingUnaccent(form.holder),
        // Tài khoản đầu tiên luôn là mặc định — ShopTrack chuyển tiền vào tài khoản mặc định.
        isDefault: banks.length === 0 ? true : form.isDefault,
      })
    },
    onSuccess: () => {
      toast.success('Đã thêm tài khoản')
      setShowForm(false)
      setForm(emptyForm())
      invalidateAll()
    },
    onError: (e) => toast.error((e as Error).message),
  })
  const remove = useMutation({
    mutationFn: (id: string) => del(`/banks/${id}`),
    onSuccess: () => { toast.success('Đã xóa'); invalidateAll() },
    onError: (e) => toast.error((e as Error).message),
  })
  const setDefault = useMutation({
    mutationFn: (id: string) => post(`/banks/${id}/set-default`),
    onSuccess: () => invalidateAll(),
    onError: (e) => toast.error((e as Error).message),
  })

  const toggle = !readOnly && !(isEmpty && !intro) && (
    <Button size="sm" variant={showForm ? 'ghost' : 'default'} onClick={() => setShowForm(!showForm)}>
      {showForm ? <><X className="size-3.5" /> Đóng</> : <> Thêm tài khoản</>}
    </Button>
  )

  return (
    <div className="space-y-6">
      {intro ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl">Tài khoản nhận tiền</h2>
          </div>
          {toggle}
        </div>
      ) : (
        toggle && <div className="flex justify-end">{toggle}</div>
      )}

      {!readOnly && showForm && (
        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate() }}
          className={cn('space-y-4', intro ? 'rounded-xl bg-raise p-5 shadow-card' : banks.length > 0 && 'rounded-lg border border-rule p-4')}
        >
          <div className="grid md:grid-cols-2 gap-4">
            <FieldRow label="Ngân hàng">
              <Select value={form.bank} onValueChange={(v) => setForm({ ...form, bank: v })}>
                <SelectTrigger><SelectValue placeholder="Chọn ngân hàng" /></SelectTrigger>
                <SelectContent>
                  {BANK_OPTIONS.map((b) => (
                    <SelectItem key={b} value={b}>
                      <span className="flex items-center gap-2.5">
                        <BankLogo bank={b} className="h-6 w-12 p-0.5" />
                        {b}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldRow>
            <FieldRow label="Số tài khoản" error={accountError}>
              <Input inputMode="numeric" value={form.accountNumber}
                aria-invalid={accountError ? true : undefined}
                onChange={(e) => setForm({ ...form, accountNumber: e.target.value.replace(/\D/g, '') })}
                placeholder="VD: 0071000123456" />
            </FieldRow>
            <FieldRow label="Tên chủ tài khoản" className="md:col-span-2">
              <Input
                value={form.holder}
                onChange={(e) => setForm({ ...form, holder: e.target.value })}
                onBlur={() => setForm((prev) => ({ ...prev, holder: toBankingUnaccent(prev.holder) }))}
                placeholder="VD: NGUYEN MAI ANH"
                autoComplete="off"
              />
            </FieldRow>
          </div>
          {banks.length > 0 && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                className="size-4 accent-accent" />
              Đặt làm mặc định
            </label>
          )}
          <Button type="submit" disabled={create.isPending || !form.accountNumber || !!accountError || !form.holder.trim()}>
            {create.isPending ? 'Đang lưu…' : 'Thêm tài khoản'}
          </Button>
        </form>
      )}

      {(banks.length > 0 || intro || readOnly || list.isLoading) && (
        <div className="rounded-lg border border-rule bg-raise divide-y divide-rule">
          {list.isLoading && <div className="p-8 text-center text-sm text-muted">Đang tải tài khoản…</div>}
          {isEmpty && (
            <div className="p-12 text-center text-inkSoft">Chưa có tài khoản</div>
          )}
          {banks.map((b, i) => (
            <div key={b.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <BankLogo bank={b.bank} className="h-10 w-16 p-1.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <IndexBadge n={i + 1} />
                    <span className="text-ink"><b>{b.bank}</b> <span className="font-mono text-inkSoft">{b.accountMasked}</span></span>
                    {b.isDefault && <span className="text-xs px-1.5 py-0.5 rounded bg-accent/12 text-accent">Mặc định</span>}
                  </div>
                  <p className="text-xs text-muted mt-0.5">
                    {b.holder}
                  </p>
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  {!b.isDefault && (
                    <Button size="sm" variant="ghost" disabled={setDefault.isPending} onClick={() => setDefault.mutate(b.id)}>
                      Đặt mặc định
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-danger hover:text-danger" disabled={remove.isPending} onClick={async () => {
                    const ok = await confirm({
                      title: 'Xóa tài khoản ngân hàng này?',
                      description: `${b.bank} ${b.accountMasked} · ${b.holder}. Tiền COD sẽ không còn chuyển về tài khoản này.${banks.length === 1 ? ' Đây là tài khoản duy nhất — xóa xong không tạo đơn được.' : ''}`,
                      tone: 'danger', confirmLabel: 'Xóa',
                    })
                    if (ok) remove.mutate(b.id)
                  }}>
                    Xóa
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
